import {
  buildFilterSearchAriaLabel,
  explorationSearchInputId,
  isExplorationSearchSectionId,
} from '~/shared/components/searchInputMeta';
import FilterSection from '~/shared/components/filters/FilterSection';

import CapacityRangeFilter from '~/shared/components/filters/CapacityRangeFilter';
import FilterCheckboxList from '~/shared/components/filters/FilterCheckboxList';
import SupportFilterList from './SupportFilterList';
import type { CheckboxDimension, ExplorationDimension } from './dimensions';
import { formatCapacityRangeBadge } from '~/shared/components/filters/capacityFormat';
import type { CapacityBounds, ExplorationFilterPanelState } from './utils/filterConverters';
import type { SectionOptions } from './utils/sectionVisibility';

interface ExplorationFilterSectionProps {
  dimension: ExplorationDimension;
  pending: ExplorationFilterPanelState;
  setField: <K extends keyof ExplorationFilterPanelState>(
    key: K
  ) => (value: ExplorationFilterPanelState[K]) => void;
  sectionOptions: SectionOptions;
  capacityBounds: CapacityBounds;
  filterSearchQuery: string;
  panelOpen: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function CheckboxSection({
  dimension,
  pending,
  setField,
  sectionOptions,
  filterSearchQuery,
  open,
  onOpenChange,
}: ExplorationFilterSectionProps & { dimension: CheckboxDimension }) {
  const { codec, stateKey } = dimension;
  const value = pending[stateKey];
  const options = sectionOptions[dimension.optionsKey] as Parameters<
    typeof FilterCheckboxList
  >[0]['options'];

  const commit = (next: unknown) => {
    (setField(stateKey) as (v: unknown) => void)(next);
  };

  return (
    <FilterSection
      title={dimension.title}
      open={open}
      onOpenChange={onOpenChange}
      selectedCount={codec.count(value)}
      onClear={() => commit(codec.clear())}
    >
      <FilterCheckboxList
        options={options}
        globalSearchQuery={dimension.respondsToGlobalSearch ? filterSearchQuery : undefined}
        showSectionSearch={dimension.showSectionSearch}
        sectionSearchInputId={
          dimension.showSectionSearch && isExplorationSearchSectionId(dimension.id)
            ? explorationSearchInputId(dimension.id)
            : undefined
        }
        sectionSearchAriaLabel={
          dimension.showSectionSearch
            ? buildFilterSearchAriaLabel(dimension.title)
            : undefined
        }
        isDisclosureOpen={open}
        isSelected={(key) => codec.has(value, key)}
        onToggle={(key) => commit(codec.toggle(value, key))}
        tagVariant={dimension.tagVariant}
        showCount={dimension.showCount}
      />
    </FilterSection>
  );
}

/**
 * Renders one filter-panel section. Uniform checkbox dimensions are driven entirely by their
 * registry entry; `support` and `capacity` keep bespoke bodies (a grouped parent/child list
 * and a range slider) inside the same shared shell.
 */
export default function ExplorationFilterSection(props: ExplorationFilterSectionProps) {
  const { dimension, pending, setField, capacityBounds, panelOpen, open, onOpenChange } = props;

  if (dimension.kind === 'checkbox') {
    return <CheckboxSection {...props} dimension={dimension} />;
  }

  if (dimension.id === 'support') {
    const setSupport = setField('supportStatuses');
    const toggleSupport = (optionId: string) => {
      setSupport(
        pending.supportStatuses.includes(optionId)
          ? pending.supportStatuses.filter((s) => s !== optionId)
          : [...pending.supportStatuses, optionId]
      );
    };
    return (
      <FilterSection
        title={dimension.title}
        open={open}
        onOpenChange={onOpenChange}
        selectedCount={pending.supportStatuses.length}
        onClear={() => setSupport([])}
      >
        <SupportFilterList
          groups={props.sectionOptions.support}
          pendingSupportStatuses={pending.supportStatuses}
          onToggleStatus={toggleSupport}
          onSetPendingSupportStatuses={setSupport}
          isDisclosureOpen={open}
        />
      </FilterSection>
    );
  }

  return (
    <FilterSection
      title={dimension.title}
      open={open}
      onOpenChange={onOpenChange}
      badgeLabel={formatCapacityRangeBadge(
        pending.capacityMin,
        pending.capacityMax,
        capacityBounds
      )}
      onClear={() => {
        setField('capacityMin')(capacityBounds.min);
        setField('capacityMax')(capacityBounds.max);
      }}
    >
      <CapacityRangeFilter
        bounds={capacityBounds}
        pendingCapacityMin={pending.capacityMin}
        pendingCapacityMax={pending.capacityMax}
        setPendingCapacityMin={setField('capacityMin')}
        setPendingCapacityMax={setField('capacityMax')}
        panelOpen={panelOpen}
        autoFocusLo={panelOpen}
      />
    </FilterSection>
  );
}
